import type {
  AgentSession,
  AgentSessionStartOptions,
} from "@/app/_blocks/BlockInteractCreateOmnibox/demos/Agents/useAgentSession";
import {
  buildLiveSpeechEngineHandoff,
  buildSpeechEngineHandoffVariables,
} from "@ui/components/demos/SpeechEngine/speechEngineAgentHandoff";
import {
  isSpeechEngineHandoffActive,
  type SpeechEngineHandoffMode,
} from "@ui/components/demos/SpeechEngine/speechEngineHandoffMode";

export type PreviewHandoff = {
  transcript: string | null;
  firstMessage: string | null;
};

export function getLayoutVisibility({
  speechEnabled,
  isSpeechExitTransition,
  hasAgentMessages,
}: {
  speechEnabled: boolean;
  isSpeechExitTransition: boolean;
  hasAgentMessages: boolean;
}) {
  const showMarketingIntro = !speechEnabled && !hasAgentMessages && !isSpeechExitTransition;
  const showChat = !speechEnabled && hasAgentMessages && !isSpeechExitTransition;
  const useSpeechLayout = speechEnabled || isSpeechExitTransition;

  return { showMarketingIntro, showChat, useSpeechLayout };
}

export function isVoiceCallConnected({
  speechEnabled,
  isHandoffPending,
  session,
}: {
  speechEnabled: boolean;
  isHandoffPending: boolean;
  session: AgentSession;
}) {
  return speechEnabled && !isHandoffPending && session.sessionIsActive && session.status === "connected";
}

export function capturePreviewHandoff(messages: AgentSession["messages"]): PreviewHandoff & { pending: boolean } {
  const handoff = buildLiveSpeechEngineHandoff(messages);
  return {
    transcript: handoff.transcript,
    firstMessage: handoff.firstMessage,
    pending: handoff.pending,
  };
}

export function buildVoiceStartOptions({
  locale,
  handoffMode,
  previewHandoff,
  handoffInstruction,
}: {
  locale: string;
  handoffMode: SpeechEngineHandoffMode;
  previewHandoff: PreviewHandoff | null;
  handoffInstruction?: string | null;
}): AgentSessionStartOptions {
  const options: AgentSessionStartOptions = { language: locale };

  if (!isSpeechEngineHandoffActive(handoffMode) || !previewHandoff) {
    return options;
  }

  if (previewHandoff.transcript) {
    options.dynamicVariables = buildSpeechEngineHandoffVariables({
      previewTranscript: previewHandoff.transcript,
      locale,
      handoffInstruction,
    });
  }

  if (handoffMode === "continueFromLastMessage" && previewHandoff.firstMessage) {
    options.firstMessage = previewHandoff.firstMessage;
  }

  return options;
}

export function endTextSession(session: AgentSession) {
  if (session.sessionIsActive) {
    session.endSession();
  }
  session.resetMessages();
}

export function formatVoiceTimerLabel(elapsedSecs: number) {
  const minutes = Math.floor(elapsedSecs / 60);
  const seconds = String(elapsedSecs % 60).padStart(2, "0");
  return `${minutes}:${seconds}`;
}
